import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n";

export default function AdminLogin() {
  const { admin, checked, login } = useAdminAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "تسجيل دخول المشرف — ARAN TOOLS";
  }, []);

  if (checked && admin) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("تم تسجيل الدخول");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 grain" dir="rtl">
      <div className="accent-glow w-96 h-96 bg-primary/20 top-0" />
      <div className="relative z-10 w-full max-w-sm">
        <div className="text-center mb-8">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 mb-4">
            <Zap className="w-6 h-6" fill="currentColor" />
          </span>
          <h1 className="font-head text-2xl font-extrabold">
            ARAN <span className="text-primary">Admin</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin_panel")}</p>
        </div>
        <form onSubmit={handleSubmit} className="aran-card p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              type="email"
              dir="ltr"
              data-testid="admin-login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 bg-background/60"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("password")}</Label>
            <Input
              id="password"
              type="password"
              dir="ltr"
              data-testid="admin-login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11 bg-background/60"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="w-full h-11 font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("login")}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground pt-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            دخول آمن — للمشرفين فقط
          </p>
        </form>
      </div>
    </div>
  );
}
